import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AiRuntimeConfigManager from "../components/settings/AiRuntimeConfigManager";
import PhoneNumberRoutingManager from "../components/settings/PhoneNumberRoutingManager";
import Button from "../components/common/Button";
import PageHeader from "../components/common/PageHeader";
import RetryBlock from "../components/common/RetryBlock";
import { useAuth } from "../context/AuthContext";
import useApiRequest from "../hooks/useApiRequest";
import useShellContext from "../layout/useShellContext";
import {
  getSettingsData,
  resetSettingsData,
  saveSettingsData,
} from "../services/settingsApi";
import { normalizeApiError } from "../utils/errors";
import { formatNow } from "../utils/formatters";
import { isAdminUser } from "../utils/roleAccess";

export default function SettingsPage() {
  const location = useLocation();
  const { registerPageControls, resetPageControls, showToast } = useShellContext();
  const { user } = useAuth();
  const { data, error, run } = useApiRequest(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const mountedRef = useRef(false);

  const loadPageData = useCallback(async ({ signal } = {}) => {
    return getSettingsData({ signal });
  }, []);

  const loadSettingsPage = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      try {
        const result = await run(loadPageData);

        if (mountedRef.current) {
          setLastUpdated(formatNow());
        }

        return result;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [loadPageData, run],
  );

  const handleRefresh = useCallback(
    () => loadSettingsPage({ trackRefresh: true }),
    [loadSettingsPage],
  );

  useEffect(() => {
    mountedRef.current = true;
    loadSettingsPage().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadSettingsPage]);

  useEffect(() => {
    if (data) {
      setDraft(data);
    }
  }, [data]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => {
      resetPageControls();
    };
  }, [handleRefresh, lastUpdated, registerPageControls, resetPageControls, shellRefreshing]);

  useEffect(() => {
    if (!draft || !location.hash) {
      return;
    }

    const targetId = location.hash.replace("#", "");
    const target = document.getElementById(targetId);

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [draft, location.hash]);

  const updateSection = (section, key, value) => {
    setDraft((current) => ({
      ...current,
      [section]: {
        ...(current?.[section] || {}),
        [key]: value,
      },
    }));
  };

  const isAdmin = isAdminUser(user);

  return (
    <>
      {error ? (
        <RetryBlock
          message="Settings are unavailable right now."
          onRetry={() => handleRefresh().catch(() => undefined)}
        />
      ) : null}

      <PageHeader
        title="Settings"
        subtitle="Manage your account, calling schedule, phone routing, and AI runtime configurations."
        actions={
          <>
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const response = await resetSettingsData();
                  setDraft(response);
                  showToast("Settings reset to the defaults.", false, 4000);
                } catch (errorValue) {
                  const normalizedError = normalizeApiError(errorValue);
                  if (!normalizedError.isAbort) {
                    showToast(normalizedError.message, true, 5000);
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              Reset Defaults
            </Button>
            <Button
              variant="gold"
              disabled={busy || !draft}
              onClick={async () => {
                if (!draft) return;
                setBusy(true);
                try {
                  const response = await saveSettingsData(draft);
                  setDraft(response);
                  setLastUpdated(formatNow());
                  showToast("Settings saved successfully.", false, 4000);
                } catch (errorValue) {
                  const normalizedError = normalizeApiError(errorValue);
                  if (!normalizedError.isAbort) {
                    showToast(normalizedError.message, true, 5000);
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save Changes
            </Button>
          </>
        }
      />

      {!draft ? (
        <div className="loading-state compact">
          <div className="loading-spinner" />
          <div className="loading-title">Loading settings...</div>
          <div className="loading-subtitle">
            Fetching account preferences and configuration.
          </div>
        </div>
      ) : (
        <div className="settings-grid">

          {/* Account Preferences */}
          <section id="account" className="chart-card settings-panel">
            <div className="settings-panel-header">
              <div className="team-card-heading">Account Preferences</div>
              <div className="page-subtitle">
                Your account identity and default landing page.
              </div>
            </div>
            <div className="settings-fields">
              <label className="settings-field">
                <span className="settings-label">Display Name</span>
                <input
                  className="settings-input"
                  value={draft.account.name}
                  onChange={(e) => updateSection("account", "name", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">Email</span>
                <input
                  className="settings-input"
                  value={draft.account.email}
                  onChange={(e) => updateSection("account", "email", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">Default Dashboard Page</span>
                <select
                  className="settings-select"
                  value={draft.account.defaultDashboard}
                  onChange={(e) => updateSection("account", "defaultDashboard", e.target.value)}
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="calls">Calls</option>
                  <option value="analytics">Analytics</option>
                  <option value="team-management">Team Management</option>
                </select>
              </label>
            </div>
          </section>

          {/* Calling Window Settings */}
          <section id="calling-window" className="chart-card settings-panel">
            <div className="settings-panel-header">
              <div className="team-card-heading">Calling Window Settings</div>
              <div className="page-subtitle">
                Define when outbound AI calls are permitted to run.
              </div>
            </div>
            <div className="settings-fields">
              <label className="settings-field">
                <span className="settings-label">Timezone</span>
                <select
                  className="settings-select"
                  value={draft.callingWindow.timezone}
                  onChange={(e) => updateSection("callingWindow", "timezone", e.target.value)}
                >
                  <option value="Asia/Dubai">Asia/Dubai (GST +4)</option>
                  <option value="Asia/Calcutta">Asia/Kolkata (IST +5:30)</option>
                  <option value="UTC">UTC</option>
                </select>
              </label>
              <label className="settings-field">
                <span className="settings-label">Working Days</span>
                <input
                  className="settings-input"
                  placeholder="e.g. Mon-Fri"
                  value={draft.callingWindow.workdays}
                  onChange={(e) => updateSection("callingWindow", "workdays", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">Start Time</span>
                <input
                  type="time"
                  className="settings-input"
                  value={draft.callingWindow.startTime}
                  onChange={(e) => updateSection("callingWindow", "startTime", e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-label">End Time</span>
                <input
                  type="time"
                  className="settings-input"
                  value={draft.callingWindow.endTime}
                  onChange={(e) => updateSection("callingWindow", "endTime", e.target.value)}
                />
              </label>
            </div>
          </section>

          {/* Admin-only sections */}
          {isAdmin ? <PhoneNumberRoutingManager showToast={showToast} /> : null}
          {isAdmin ? <AiRuntimeConfigManager showToast={showToast} /> : null}
        </div>
      )}
    </>
  );
}
